import { useState } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Space, Popconfirm, message, List, Grid, Card, Typography, Descriptions } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { Contractor, ContractorPrice } from '../types/contractor';
import { crmApi } from '../../../api/client';

const { Text } = Typography;

interface ContractorPricingTableProps {
    contractor: Contractor;
    onUpdate: () => void;
}

export const ContractorPricingTable = ({ contractor, onUpdate }: ContractorPricingTableProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [editingPrice, setEditingPrice] = useState<ContractorPrice | null>(null);
    const [selectedPrice, setSelectedPrice] = useState<ContractorPrice | null>(null);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;

    const handleOpenModal = (price?: ContractorPrice) => {
        if (price) {
            setEditingPrice(price);
            form.setFieldsValue(price);
        } else {
            setEditingPrice(null);
            form.resetFields();
        }
        setIsModalOpen(true);
    };

    const handleShowDetail = (price: ContractorPrice) => {
        setSelectedPrice(price);
        setIsDetailModalOpen(true);
    };

    const handleSubmit = async (values: any) => {
        setLoading(true);
        try {
            if (editingPrice) {
                await crmApi.updateContractorPrice(editingPrice.id, values);
                message.success('Zaktualizowano cenę');
            } else {
                await crmApi.addContractorPrice(contractor.id, values);
                message.success('Dodano cenę');
            }
            setIsModalOpen(false);
            onUpdate();
        } catch (error) {
            message.error('Błąd zapisu ceny');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        try {
            await crmApi.deleteContractorPrice(id);
            message.success('Usunięto cenę');
            setIsDetailModalOpen(false);
            onUpdate();
        } catch (error) {
            message.error('Błąd usuwania ceny');
        }
    };

    const columns = [
        {
            title: 'SKU',
            key: 'sku',
            render: (_: any, record: ContractorPrice) => (
                <a onClick={() => handleShowDetail(record)}>{record.sku}</a>
            ),
        },
        { title: 'Cena ASP', dataIndex: 'asp', key: 'asp', render: (val: number) => val.toFixed(2) },
    ];

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal()} block={isMobile}>
                    Dodaj cenę
                </Button>
            </div>

            {isMobile ? (
                <List
                    dataSource={contractor.prices}
                    renderItem={(item) => (
                        <Card size="small" style={{ marginBottom: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Text strong onClick={() => handleShowDetail(item)}>{item.sku}</Text>
                                <Space>
                                    <Button size="small" type="text" icon={<EditOutlined />} onClick={() => handleOpenModal(item)} />
                                </Space>
                            </div>
                        </Card>
                    )}
                />
            ) : (
                <Table
                    dataSource={contractor.prices}
                    columns={columns}
                    rowKey="id"
                    pagination={false}
                />
            )}

            {/* Modal Szczegółów */}
            <Modal
                title="Szczegóły ceny"
                open={isDetailModalOpen}
                onCancel={() => setIsDetailModalOpen(false)}
                footer={[
                    <Button key="edit" icon={<EditOutlined />} onClick={() => { setIsDetailModalOpen(false); handleOpenModal(selectedPrice!); }}>Edytuj</Button>,
                    <Popconfirm key="del" title="Usunąć?" onConfirm={() => selectedPrice && handleDelete(selectedPrice.id)}><Button danger icon={<DeleteOutlined />}>Usuń</Button></Popconfirm>
                ]}
                width={400}
            >
                {selectedPrice && (
                    <Descriptions column={1} bordered size="small">
                        <Descriptions.Item label="SKU">{selectedPrice.sku}</Descriptions.Item>
                        <Descriptions.Item label="Cena ASP">{selectedPrice.asp.toFixed(2)}</Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>

            {/* Modal Formularza */}
            <Modal
                title={editingPrice ? 'Edytuj cenę' : 'Nowa cena'}
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                onOk={() => form.submit()}
                width={400}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item name="sku" label="SKU" rules={[{ required: true }]}><Input /></Form.Item>
                    <Form.Item name="asp" label="Cena ASP" rules={[{ required: true }]}><InputNumber min={0} step={0.01} style={{ width: '100%' }} /></Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
